import { ColumnConfig } from "../../../../List/schema/columnConfig";

/**
 * Row model of the AI snippets section: a snippet is either a column that
 * already lives on the list (insertable as-is) or a company-wide template
 * (must be added to the list as a column before its token can be inserted).
 */
export type SnippetRow =
	| {
			kind: "listColumn";
			key: string;
			name: string;
			columnId: number;
			column: ColumnConfig;
			isInserted: boolean;
	  }
	| {
			kind: "template";
			key: string;
			name: string;
			template: ColumnConfig;
	  };

export const normalizeSnippetName = (name?: string | null): string =>
	(name ?? "").trim().toLowerCase();

export const templateRowKey = (template: ColumnConfig): string =>
	`tpl-${template.id ?? normalizeSnippetName(template.name)}`;

interface BuildSnippetRowsArgs {
	/** AI_SNIPPET columns of the current list, in list order. */
	listColumns: ColumnConfig[];
	/** Columns just created from templates this session (optimistic copies). */
	createdColumns: ColumnConfig[];
	/** Company-wide AI snippet column templates. */
	templates: ColumnConfig[];
	/** Column ids referenced by the current message bodies. */
	insertedColumnIds: number[];
}

const toListColumnRow = (column: ColumnConfig, insertedColumnIds: number[]): SnippetRow => ({
	kind: "listColumn",
	key: `col-${column.id}`,
	name: column.name ?? "",
	columnId: column.id as number,
	column,
	isInserted: column.id != null && insertedColumnIds.includes(column.id),
});

/**
 * Merges list columns, optimistically created columns and templates into the
 * section's row list: columns first (list order), then templates that are not
 * on the list yet. A template counts as "on the list" when a column links to
 * it via `templateId`, or — for legacy columns created before the link existed
 * — when a column carries the same normalized name.
 */
export const buildSnippetRows = ({
	listColumns,
	createdColumns,
	templates,
	insertedColumnIds,
}: BuildSnippetRowsArgs): SnippetRow[] => {
	const knownColumnIds = new Set(listColumns.map((c) => c.id));

	// Created columns collapse into the list once the refetched list carries them.
	const pendingCreated = createdColumns.filter((c) => !knownColumnIds.has(c.id));

	const columns = [...listColumns, ...pendingCreated].filter((c) => c.id != null);

	const linkedTemplateIds = new Set(
		columns.map((c) => c.templateId).filter((id): id is number => id != null)
	);
	const columnNames = new Set(columns.map((c) => normalizeSnippetName(c.name)));

	const availableTemplates = templates.filter(
		(template) =>
			!(template.id != null && linkedTemplateIds.has(template.id)) &&
			!columnNames.has(normalizeSnippetName(template.name))
	);

	return [
		...columns.map((column) => toListColumnRow(column, insertedColumnIds)),
		...availableTemplates.map(
			(template): SnippetRow => ({
				kind: "template",
				key: templateRowKey(template),
				name: template.name ?? "",
				template,
			})
		),
	];
};
